import type { PrototypeApiCall } from "./apiContracts";
import { redactForLog } from "./requestLogRedactor";
import { buildHeaders, buildAnonymousHeader, readJsonSafely, isTransientNetworkError, sleep } from "./apiTransportInternals";
import { takeAccessDecisionTraceHeaders } from './accessDecisionTrace';
import type { TransportAttemptFailure } from "./clientSessionDiagnostics";

/** Network-level timeout for a single fetch attempt. */
const REQUEST_TIMEOUT_MS = 15_000;
/** Raw attachment uploads are allowed to use the full source-attachment window. */
const UPLOAD_REQUEST_TIMEOUT_MS = 120_000;
/** Max retry attempts for idempotent (GET) requests on transient failures. */
const MAX_RETRIES = 2;
/** Base backoff (ms); actual delay = base * 3^attempt (300ms, 900ms). */
const RETRY_BASE_DELAY_MS = 300;
/** HTTP statuses considered transient and safe to retry for GET. */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
/** Explicit server codes that mean the authenticated cookie is no longer usable. */
const SESSION_EXPIRY_ERROR_CODES = new Set(['authentication_required', 'session_revoked', 'unauthorized']);

/**
 * Branding / host-specific identifiers. Injected via constructor so the
 * transport module is portable — strip the deepmox context and it still
 * works in any SPA that needs retry + timeout + call logging.
 */
export interface TransportConfig {
	/** Label used in error messages, e.g. "Deepmox API". */
	readonly errorLabel: string;
	/** localStorage key for the anonymous visitor id. */
	readonly anonymousIdStorageKey: string;
	/** HTTP header name carrying the anonymous visitor id. */
	readonly anonymousIdHeader: string;
	/** Optional: expose the call log on `window[globalCallsKey]` for devtools / E2E. */
	readonly globalCallsKey?: string;
	/**
	 * Optional evidence hook fired once per failed request attempt (after the
	 * retry loop's caller-abort filtering). Unlike `calls` (final outcome only,
	 * visible only with DevTools open), this reaches the host's diagnostics
	 * recorder so outages leave server-side evidence without F12.
	 */
	readonly onRequestAttemptFailed?: (info: TransportAttemptFailure) => void;
}

const DEFAULT_CONFIG: TransportConfig = {
	errorLabel: "API",
	anonymousIdStorageKey: "anonymous-id",
	anonymousIdHeader: "x-anonymous-id",
};

export class PrototypeApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly path: string,
    readonly body: unknown,
	readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "PrototypeApiError";
  }
}

export class PrototypeApiTransport {
  private nextId = 1;
  readonly calls: PrototypeApiCall[] = [];
  protected readonly config: TransportConfig;
  /**
   * Optional callback fired when an authenticated request receives the
   * server's explicit session-expiry response. For cookie-based auth there is
   * no refresh token to replay silently — the session cookie has expired
   * or been revoked.  The host app wires this to a login prompt so the
   * user is re-authenticated rather than seeing silent failures pile up
   * across background data loaders that don't go through handleApiError.
   */
  onSessionExpired?: () => void;
  private authenticated = false;

  /** Host app calls this after bootstrap to tell the transport whether
   *  the current viewer is logged in. When true, a session-expiry response
   *  means the session expired mid-flight and
   *  onSessionExpired is fired so the UI can prompt re-login. */
  setAuthenticated(value: boolean): void {
    this.authenticated = value;
  }

  constructor(baseUrl = "", config: TransportConfig = DEFAULT_CONFIG) {
    this.baseUrl = baseUrl;
    this.config = config;
  }

  protected baseUrl: string;

  protected get(path: string, signal?: AbortSignal) {
    return this.request("GET", path, undefined, signal);
  }

  protected post(path: string, body: unknown, signal?: AbortSignal) {
    return this.request("POST", path, body, signal);
  }

	protected postWithHeaders(path: string, body: unknown, headers: Record<string, string>) {
		return this.request('POST', path, body, undefined, false, headers);
	}

  /**
   * Sends a small, non-critical mutation that must be allowed to finish when
   * the browser navigates away. Keepalive POSTs are never retried and must not
   * be used for uploads or mutations whose response drives the UI.
   */
  protected postBackground(path: string, body: unknown) {
    return this.request("POST", path, body, undefined, true);
  }

  protected del(path: string, signal?: AbortSignal) {
    return this.request("DELETE", path, undefined, signal);
  }

	private async request(method: string, path: string, requestBody?: unknown, callerSignal?: AbortSignal, keepalive = false, extraHeaders?: Record<string, string>) {
    const id = this.nextId++;
    const startedAt = new Date().toISOString();
    let status = 0;
    let responseBody: unknown;
    let error: string | undefined;

    // Only GET is safe to retry — POST mutations may have side effects.
    const canRetry = method === "GET";
    const maxAttempts = canRetry ? MAX_RETRIES + 1 : 1;
	const requestHeaders = {
		...(buildHeaders(requestBody !== undefined, this.config) as Record<string, string> | undefined),
		...takeAccessDecisionTraceHeaders(),
		...extraHeaders,
	};

    // If the caller already aborted before we even started, bail immediately.
    if (callerSignal?.aborted) throw new DOMException("Aborted", "AbortError");

    try {
      let response: Response | null = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const attemptStartedAt = Date.now();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        // Link the caller's abort signal: if the caller aborts (e.g. chapter
        // switch), cancel the in-flight fetch immediately. We never abort
        // the timeout controller directly — only via this linkage.
        const onCallerAbort = () => controller.abort();
        callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
        try {
          response = await fetch(`${this.baseUrl}${path}`, {
            method,
            headers: requestHeaders,
            body: requestBody === undefined ? undefined : JSON.stringify(requestBody),
            signal: controller.signal,
			keepalive,
          });
        } catch (fetchError) {
          // Network-level failure (TypeError) or abort — retry if GET and attempts remain.
          clearTimeout(timeoutId);
          callerSignal?.removeEventListener("abort", onCallerAbort);
          // If the caller aborted, don't retry — propagate immediately.
          if (callerSignal?.aborted) throw fetchError;
          // The remaining AbortError comes from this attempt's 15s timeout controller.
          this.emitAttemptFailure(method, path, attempt, maxAttempts, attemptStartedAt,
            fetchError instanceof Error && fetchError.name === "AbortError" ? "timeout" : "network", 0, fetchError);
          if (canRetry && attempt < maxAttempts - 1 && isTransientNetworkError(fetchError)) {
            await sleep(RETRY_BASE_DELAY_MS * 3 ** attempt);
            continue;
          }
          throw fetchError;
        }
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener("abort", onCallerAbort);

        // Retry on transient 5xx only for GET, and only if attempts remain.
        if (canRetry && TRANSIENT_STATUSES.has(response.status) && attempt < maxAttempts - 1) {
          this.emitAttemptFailure(method, path, attempt, maxAttempts, attemptStartedAt, "http", response.status);
          await sleep(RETRY_BASE_DELAY_MS * 3 ** attempt);
          continue;
        }
        if (!response.ok) {
          this.emitAttemptFailure(method, path, attempt, maxAttempts, attemptStartedAt, "http", response.status);
        }
        break; // success or non-retryable status
      }

      status = response!.status;
      responseBody = await readJsonSafely(response!);
      const call = this.record({
        id,
        method,
        path,
        ok: response!.ok,
        status,
        startedAt,
        finishedAt: new Date().toISOString(),
        requestBody,
        responseBody,
      });
      if (!response!.ok) {
        // Session expired mid-flight: the user was authenticated (per
        // bootstrap) but the server rejected the session. Fire onSessionExpired
        // exactly once so the host app can prompt re-login.  Visitors
        // only for the explicit session-expiry allowlist. Other 401s
        // (wrong password, provider challenge, resource policy) stay local to
        // their form instead of falsely logging out a valid session.
        const responseError = responseBody && typeof responseBody === 'object' && 'error' in responseBody
          ? (responseBody as { error?: unknown }).error
          : null;
        if (status === 401 && typeof responseError === 'string' && SESSION_EXPIRY_ERROR_CODES.has(responseError) && this.authenticated) {
          this.authenticated = false;
          this.onSessionExpired?.();
        }
        throw new PrototypeApiError(
			`${this.config.errorLabel} ${method} ${path} returned ${status}`,
			status,
			path,
			responseBody,
			parseRetryAfterSeconds(response!.headers.get('retry-after')),
		);
      }
      return call.responseBody;
    } catch (caught) {
      if (caught instanceof PrototypeApiError) throw caught;
      error = caught instanceof Error ? caught.message : String(caught);
      this.record({
        id,
        method,
        path,
        ok: false,
        status,
        startedAt,
        finishedAt: new Date().toISOString(),
        requestBody,
        responseBody,
        error,
      });
      throw new PrototypeApiError(`${this.config.errorLabel} ${method} ${path} failed: ${error}`, status, path, responseBody);
    }
  }

  /**
   * Raw PUT to a same-origin /api/uploads/put URL. Records into the
   * `calls` log so devtools / E2E can introspect. The body is sent as
   * raw bytes; the server infers the content-type from the request
   * header. No JSON parsing — we return { ok, status }.
   *
   * Unlike `request()` this is never retried — PUT has side effects. But it
   * uses a dedicated upload timeout + caller-abort linkage: a stalled R2
   * presign URL is aborted after UPLOAD_REQUEST_TIMEOUT_MS, and a caller-supplied
   * signal (e.g. the user navigating away mid-upload) cancels the in-flight
   * PUT immediately.
   */
  async putToUrl(url: string, body: Blob | ArrayBuffer, contentType: string, callerSignal?: AbortSignal): Promise<{ ok: boolean; status: number; body: unknown }> {
    const id = this.nextId++;
    const startedAt = new Date().toISOString();
    const path = url.startsWith(this.baseUrl) ? url.slice(this.baseUrl.length) : url;

    // If the caller already aborted before we even started, bail immediately.
    if (callerSignal?.aborted) throw new DOMException("Aborted", "AbortError");

    try {
      // Per-attempt timeout controller, linked to the caller's abort signal
      // so either source cancels the in-flight fetch. Mirrors `request()`.
      const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), UPLOAD_REQUEST_TIMEOUT_MS);
      const onCallerAbort = () => controller.abort();
      callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
      let response: Response;
      try {
        response = await fetch(url, {
          method: "PUT",
          headers: {
            "content-type": contentType,
            ...buildAnonymousHeader(this.config),
          },
          body,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener("abort", onCallerAbort);
      }
      const responseBody = await readJsonSafely(response);
      this.record({
        id,
        method: "PUT",
        path,
        ok: response.ok,
        status: response.status,
        startedAt,
        finishedAt: new Date().toISOString(),
        requestBody: { size: body instanceof ArrayBuffer ? body.byteLength : (body as Blob).size, contentType },
        responseBody,
      });
      if (!response.ok) {
        throw new PrototypeApiError(
          `${this.config.errorLabel} upload PUT ${path} returned ${response.status}`,
          response.status,
          path,
          responseBody,
        );
      }
      return { ok: response.ok, status: response.status, body: responseBody };
    } catch (caught) {
      if (caught instanceof PrototypeApiError) throw caught;
      const message = caught instanceof Error ? caught.message : String(caught);
      this.record({
        id,
        method: "PUT",
        path,
        ok: false,
        status: 0,
        startedAt,
        finishedAt: new Date().toISOString(),
        requestBody: { contentType },
        responseBody: null,
        error: message,
      });
      throw new PrototypeApiError(`${this.config.errorLabel} upload PUT ${path} failed: ${message}`, 0, path, null);
    }
  }

  private record(call: PrototypeApiCall) {
    const sanitized: PrototypeApiCall = {
      ...call,
      requestBody: redactForLog(call.requestBody),
    };
    this.calls.push(sanitized);
    if (this.config.globalCallsKey) {
      (window as unknown as Record<string, unknown>)[this.config.globalCallsKey] = this.calls;
    }
    return sanitized;
  }

  /** Evidence-only telemetry for failed GET attempts; mutations (login 401s etc.) stay out of the buffer. */
  private emitAttemptFailure(
    method: string,
    path: string,
    attempt: number,
    maxAttempts: number,
    startedAtMs: number,
    outcome: TransportAttemptFailure['outcome'],
    status: number,
    error?: unknown,
  ): void {
    if (method !== 'GET' || !this.config.onRequestAttemptFailed) return;
    const name = error instanceof Error ? error.name : undefined;
    const message = error instanceof Error ? error.message : undefined;
    this.config.onRequestAttemptFailed({
      method,
      path,
      attempt: attempt + 1,
      maxAttempts,
      outcome,
      status,
      errorName: name,
      errorMessage: message,
      durationMs: Date.now() - startedAtMs,
    });
  }
}

function parseRetryAfterSeconds(value: string | null): number | undefined {
	const normalized = value?.trim();
	if (!normalized) return undefined;
	if (/^\d+(?:\.\d+)?$/u.test(normalized)) {
		const seconds = Number(normalized);
		return Number.isFinite(seconds) ? Math.max(0, Math.ceil(seconds)) : undefined;
	}
	const retryAt = Date.parse(normalized);
	if (!Number.isFinite(retryAt)) return undefined;
	return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}
